import { createApi } from '@reduxjs/toolkit/query/react';

import { apiBaseUrl } from '../Api';
import { makeBaseQuery } from '../baseQuery';
import type {
  UnlockOrderResult,
  UnlockStatusResult,
  VerifyUnlockPaymentArgs
} from './paymentTypes';

const asRecord = (value: unknown): Record<string, unknown> =>
  typeof value === 'object' && value !== null ? (value as Record<string, unknown>) : {};

const envelope = (response: unknown): Record<string, unknown> => asRecord(asRecord(response).data);

const asNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim() !== '' && Number.isFinite(Number(value))) {
    return Number(value);
  }
  return null;
};

const asString = (value: unknown): string | null => (typeof value === 'string' ? value : null);

export function normalizeUnlockOrder(response: unknown): UnlockOrderResult {
  const data = envelope(response);
  return {
    alreadyUnlocked: data.alreadyUnlocked === true,
    unlockId: asNumber(data.unlockId),
    unlockedAt: asString(data.unlockedAt),
    orderId: asString(data.orderId),
    amount: asNumber(data.amount),
    currency: asString(data.currency),
    keyId: asString(data.keyId)
  };
}

export function normalizeUnlockStatus(response: unknown): UnlockStatusResult {
  const data = envelope(response);
  return { unlocked: data.unlocked === true, unlockedAt: asString(data.unlockedAt) };
}

export const paymentApi = createApi({
  reducerPath: 'paymentApi',
  baseQuery: makeBaseQuery(apiBaseUrl),
  tagTypes: ['Unlock'],
  endpoints: (builder) => ({
    createUnlockOrder: builder.mutation<UnlockOrderResult, { id: number }>({
      query: ({ id }) => ({ url: `buyer/properties/${id}/unlock`, method: 'POST' }),
      transformResponse: (response: unknown) => normalizeUnlockOrder(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Unlock', id: String(arg.id) }]
    }),
    verifyUnlockPayment: builder.mutation<UnlockStatusResult, VerifyUnlockPaymentArgs>({
      query: ({ id, razorpayOrderId, razorpayPaymentId, razorpaySignature }) => ({
        url: `buyer/properties/${id}/unlock/verify`,
        method: 'POST',
        body: { razorpayOrderId, razorpayPaymentId, razorpaySignature }
      }),
      transformResponse: (response: unknown) => normalizeUnlockStatus(response),
      invalidatesTags: (_result, _error, arg) => [{ type: 'Unlock', id: String(arg.id) }]
    }),
    getUnlockStatus: builder.query<UnlockStatusResult, { id: number }>({
      query: ({ id }) => ({ url: `buyer/properties/${id}/unlock` }),
      transformResponse: (response: unknown) => normalizeUnlockStatus(response),
      providesTags: (_result, _error, arg) => [{ type: 'Unlock', id: String(arg.id) }]
    })
  })
});

export const {
  useCreateUnlockOrderMutation,
  useVerifyUnlockPaymentMutation,
  useGetUnlockStatusQuery
} = paymentApi;
